/**
 * `useHolderQuery` - TanStack Query wrapper around
 * `BffClient.getHolderByUsername`.
 *
 * The detail page's Holder region resolves the holder from the account's
 * `legacy_username` (the primary Murmuration flow - the account is already in
 * context and carries the cross-FK). The query is therefore gated on the
 * account having loaded: `enabled` is false until a non-empty
 * `legacyUsername` is supplied, so we never fire a `/holders` lookup with
 * an empty selector.
 *
 * 404 surfaces as a `BffError` with `status=404`; the Holder region
 * renders an "No holder record" empty state rather than an error. As with
 * `useAccountQuery`, 401/403 fall through to the shared error provider and
 * 4xx are not retried.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import type { BffClient, Holder } from '../../../lib/bff-client'

export interface UseHolderQueryArgs {
  readonly client: BffClient
  /** The account's `legacy_username`; the query is disabled until set. */
  readonly legacyUsername: string | null | undefined
}

export function holderQueryKey(legacyUsername: string): readonly unknown[] {
  return ['holder', 'by-username', legacyUsername] as const
}

export function useHolderQuery({
  client,
  legacyUsername,
}: UseHolderQueryArgs): UseQueryResult<Holder, Error> {
  const enabled = typeof legacyUsername === 'string' && legacyUsername !== ''
  return useQuery({
    queryKey: holderQueryKey(legacyUsername ?? ''),
    queryFn: () => client.getHolderByUsername(legacyUsername as string),
    enabled,
    staleTime: 30_000,
    retry: (failureCount, err) => {
      const status = (err as { status?: number }).status
      if (status === 401 || status === 403 || status === 400 || status === 404) {
        return false
      }
      return failureCount < 1
    },
    throwOnError: false,
  })
}
